import React, { useState, useMemo } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import CalendarDay from './CalendarDay';
import { 
  startOfMonth, 
  endOfMonth, 
  startOfWeek, 
  endOfWeek, 
  addDays, 
  format, 
  isSameMonth, 
  isSameDay, 
  addMonths,
  subMonths
} from 'date-fns';
import { tr } from 'date-fns/locale';

const CalendarView = ({ 
  tickets = [], 
  selectedProject,
  onTicketClick = () => {},
  onDragEnd = () => {}
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showPopup, setShowPopup] = useState(false);
  const [popupTickets, setPopupTickets] = useState([]);
  const [popupDate, setPopupDate] = useState(null);
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });

  // Ay değiştirme fonksiyonları
  const goToPreviousMonth = () => {
    setCurrentMonth(prev => subMonths(prev, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(prev => addMonths(prev, 1));
  };

  const goToToday = () => {
    const today = new Date();
    setCurrentMonth(today);
    setSelectedDate(today);
  };

  // Sürükle-bırak işlemi
  const handleDragEnd = (result) => {
    const { destination, source, draggableId } = result;

    // Eğer geçerli bir hedef yoksa işlemi iptal et
    if (!destination) {
      return;
    }

    // Aynı yerde bırakıldıysa işlemi iptal et
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    // Ticket ID'sini ve hedef tarihi al
    const ticketId = draggableId;
    const targetDate = new Date(destination.droppableId);
    
    // onDragEnd callback'ini çağır (parent component'te implement edilecek)
    if (onDragEnd) {
      onDragEnd(ticketId, targetDate);
    }
  };

  // Takvim grid'ini oluştur
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const startDate = startOfWeek(monthStart, { weekStartsOn: 1 }); // Pazartesi başlangıç
    const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });

    const days = [];
    let day = startDate;

    while (day <= endDate) {
      days.push(day);
      day = addDays(day, 1);
    }

    return days;
  }, [currentMonth]);

  // Belirli bir tarihte olan ticket'ları getir
  const getTicketsForDate = (date) => {
    return tickets.filter(ticket => {
      // Önce due_date'i kontrol et, yoksa created_at'i kullan
      if (ticket.due_date) {
        // due_date string formatında (YYYY-MM-DD) geliyor, timezone sorununu önlemek için
        const ticketDateStr = ticket.due_date;
        const targetDateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        return ticketDateStr === targetDateStr;
      } else {
        // created_at veya updated_at için normal tarih karşılaştırması
        const ticketDate = new Date(ticket.created_at || ticket.updated_at);
        return isSameDay(ticketDate, date);
      }
    });
  };

  // Haftanın günleri (Pazartesi başlangıç)
  const weekDays = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'];

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
      {/* Takvim Başlığı - Sağ üstte */}
      <div className="flex items-center justify-between p-3 border-b border-gray-200 dark:border-gray-700">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {selectedProject?.project_name} - Takvim
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Proje ticket'larını takvim görünümünde inceleyin • Ticket'ları sürükleyerek tarih değiştirebilirsiniz
          </p>
        </div>
        
        {/* Sağ: Bugün butonu ve ay navigasyonu */}
        <div className="flex items-center space-x-4">
          <button
            onClick={goToToday}
            className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
          >
            Bugün
          </button>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={goToPreviousMonth}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title="Önceki Ay"
            >
              <ChevronLeftIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </button>
            
            <h3 className="text-lg font-medium text-gray-900 dark:text-white min-w-[160px] text-center">
              {format(currentMonth, 'MMMM yyyy', { locale: tr })}
            </h3>
            
            <button
              onClick={goToNextMonth}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title="Sonraki Ay"
            >
              <ChevronRightIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </button>
          </div>
        </div>
      </div>

      {/* Haftanın Günleri */}
      <div className="grid grid-cols-7 gap-0 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
        {weekDays.map((day) => (
          <div
            key={day}
            className="p-3 text-center text-sm font-medium text-gray-700 dark:text-gray-300 border-r border-gray-200 dark:border-gray-700 last:border-r-0"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Takvim Grid */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="grid grid-cols-7 gap-0">
          {calendarDays.map((day, index) => {
            const dayTickets = getTicketsForDate(day);
            const isCurrentMonth = isSameMonth(day, currentMonth);
            const isSelected = isSameDay(day, selectedDate);
            const isToday = isSameDay(day, new Date());

            return (
              <Droppable key={index} droppableId={day.toISOString()}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    onClick={() => {
                      setSelectedDate(day);
                      if (dayTickets.length > 0) {
                        // İlk ticket'ı aç
                        onTicketClick(dayTickets[0]);
                      }
                    }}
                    className={`
                      relative min-h-[110px] p-2 cursor-pointer transition-all duration-200 border-r border-b border-gray-200 dark:border-gray-700 last:border-r-0
                      ${isCurrentMonth 
                        ? 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700' 
                        : 'bg-gray-50 dark:bg-gray-900 text-gray-400 dark:text-gray-600'
                      }
                      ${isSelected 
                        ? 'ring-2 ring-blue-500 ring-inset bg-blue-50 dark:bg-blue-900/20' 
                        : ''
                      }
                      ${isToday 
                        ? 'bg-blue-50 dark:bg-blue-900/20' 
                        : ''
                      }
                      ${snapshot.isDraggingOver 
                        ? 'bg-blue-100 dark:bg-blue-900/30 border-2 border-blue-400 dark:border-blue-500 ring-2 ring-blue-200 dark:ring-blue-800 ring-opacity-50' 
                        : ''
                      }
                    `}
                  >
                    {/* Gün Numarası */}
                    <div className="flex items-center justify-between mb-1">
                      <span
                        className={`
                          text-base font-semibold
                          ${isCurrentMonth 
                            ? 'text-gray-900 dark:text-white' 
                            : 'text-gray-400 dark:text-gray-600'
                          }
                          ${isToday ? 'text-blue-600 dark:text-blue-400' : ''}
                        `}
                      >
                        {format(day, 'd')}
                      </span>
                      
                      {/* Bugün işareti */}
                      {isToday && (
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      )}
                    </div>

                    {/* Ticket Listesi */}
                    {dayTickets.length > 0 && (
                      <div className="space-y-1">
                        {dayTickets.slice(0, 2).map((ticket, ticketIndex) => {
                          const priority = ticket.priority ? ticket.priority.toString().toLowerCase() : '';
                          let colorClass = 'bg-blue-500 text-white border-l-4 border-blue-600';
                          
                          if (priority === 'high' || priority === 'yüksek') {
                            colorClass = 'bg-red-500 text-white border-l-4 border-red-600';
                          } else if (priority === 'medium' || priority === 'orta') {
                            colorClass = 'bg-yellow-500 text-white border-l-4 border-yellow-600';
                          } else if (priority === 'low' || priority === 'düşük') {
                            colorClass = 'bg-green-500 text-white border-l-4 border-green-600';
                          }

                          return (
                            <Draggable 
                              key={ticket.id} 
                              draggableId={String(ticket.id)} 
                              index={ticketIndex}
                            >
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  className={`
                                    text-xs px-2 py-1.5 rounded-r-md truncate font-medium ${colorClass} shadow-sm cursor-grab
                                    ${snapshot.isDragging 
                                      ? 'opacity-70 rotate-3 scale-110 shadow-2xl z-50' 
                                      : 'hover:shadow-lg hover:scale-105'
                                    }
                                    transition-all duration-300 ease-in-out
                                    active:cursor-grabbing
                                  `}
                                  title={ticket.title || ticket.name}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onTicketClick(ticket);
                                  }}
                                >
                                  #{ticket.id || 'N/A'} - {ticket.title || ticket.name}
                                </div>
                              )}
                            </Draggable>
                          );
                        })}
                        
                        {dayTickets.length > 2 && (
                          <div 
                            className="text-xs text-gray-500 dark:text-gray-400 px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              const rect = e.currentTarget.getBoundingClientRect();
                              setPopupPosition({ 
                                x: rect.left, 
                                y: rect.top - 10 
                              });
                              setPopupTickets(dayTickets);
                              setPopupDate(day);
                              setShowPopup(true);
                            }}
                          >
                            +{dayTickets.length - 2} daha
                          </div>
                        )}
                      </div>
                    )}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            );
          })}
        </div>
      </DragDropContext>

      {/* Alt Bilgi */}
      <div className="p-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
        <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
          <div className="flex items-center space-x-6">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-red-500 rounded"></div>
              <span>Yüksek Öncelik</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-yellow-500 rounded"></div>
              <span>Orta Öncelik</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-green-500 rounded"></div>
              <span>Düşük Öncelik</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-blue-500 rounded"></div>
              <span>Normal</span>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <span>Toplam {tickets.length} ticket</span>
            <div className="flex items-center space-x-1 text-xs">
              <span className="text-gray-500">💡</span>
              <span className="text-gray-500">Sürükle-bırak ile tarih değiştir</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tooltip Popup */}
      {showPopup && (
        <>
          {/* Arka plan overlay */}
          <div 
            className="fixed inset-0 z-40"
            onClick={() => setShowPopup(false)}
          />
          
          {/* Popup penceresi */}
          <div 
            className="fixed z-50 bg-white dark:bg-gray-800 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-700 w-72 max-h-80 overflow-hidden"
            style={{
              left: `${popupPosition.x}px`,
              top: `${popupPosition.y}px`,
              transform: 'translateY(-100%)'
            }}
          >
            {/* Başlık */}
            <div className="flex items-center justify-between p-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
              <h3 className="text-xs font-medium text-gray-900 dark:text-white">
                {popupDate && format(popupDate, 'dd MMMM', { locale: tr })} ({popupTickets.length.toString()})
              </h3>
              <button
                onClick={() => setShowPopup(false)}
                className="p-0.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
              >
                <XMarkIcon className="w-3 h-3 text-gray-500" />
              </button>
            </div>

            {/* Ticket Listesi */}
            <div className="overflow-y-auto max-h-64">
              {popupTickets.map((ticket, index) => (
                <div
                  key={index}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer transition-colors border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                  onClick={() => {
                    onTicketClick(ticket);
                    setShowPopup(false);
                  }}
                >
                  <div className="text-xs text-gray-900 dark:text-white font-medium truncate">
                    #{ticket.id || 'N/A'} - {ticket.title || ticket.name || 'İsimsiz Ticket'}
                  </div>
                </div>
              ))}
            </div>
            
            {/* Alt ok işareti */}
            <div className="absolute bottom-0 left-4 transform translate-y-full">
              <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-200 dark:border-t-gray-700"></div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CalendarView;
